import { useMemo, useEffect, memo, useState } from 'react';
import Bluebird from 'bluebird';
import { capitalize } from 'lodash';
import { Table, TBody, THead, TH, TableRow } from '@/components/DesignLibrary/Table';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import Filter from '@/components/Filter';
import Header from '@/components/DesignLibrary/Header';
import ProgressIndicator from '@/components/ProgressIndicator';
import Spacer from '@/components/Spacer';
import Note from '@components/Icons/Note';
import AddUserWorkflowNoteModal from '@modals/AddUserWorkflowNoteModal';
import ItemRow from './ItemRow';
import { columns, FILTERS } from './constants';

const MemoizedItemRow = memo(ItemRow);

const UserWorkflowActivityThread: React.FC<{ userWorkflowId: string }> = ({ userWorkflowId }) => {
  const [search, setSearch] = useState<{
    searchBy: string;
    startDate: string | null;
    endDate: string | null;
  }>({
    endDate: null,
    searchBy: '',
    startDate: null,
  });
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
  const { data, fetchingState, getData: getUserWorkflowActivityById } = useNoodleApi(tsClient.workflows.getUserWorkflowActivityById);

  const { data: userWorkflowData, getData: getUserWorkflowFn } = useNoodleApi(tsClient.workflows.getUserWorkflowById, { toastOnError: true });

  const { data: collaborators, getData: getCollaboratorsFn } = useNoodleApi(tsClient.workflows.getUserWorkflowCollaborators, { toastOnError: true });

  useEffect(() => {
    if (userWorkflowId) {
      getUserWorkflowFn({
        userWorkflowId,
      });
      getCollaboratorsFn({ userWorkflowId });
    }
  }, [userWorkflowId, getUserWorkflowFn, getCollaboratorsFn]);

  useEffect(() => {
    if (userWorkflowId) {
      getUserWorkflowActivityById({
        endDate: search.endDate || undefined,
        filter: FILTERS[selectedFilterIndex].value,
        page: 0,
        perPage: 1000,
        search: search.searchBy,
        startDate: search.startDate || undefined,
        userWorkflowId,
      });
    }
  }, [userWorkflowId, getUserWorkflowActivityById, selectedFilterIndex, search]);

  useEffect(() => {
    if (userWorkflowId) {
      getUserWorkflowFn({ userWorkflowId });
    }
  }, [userWorkflowId, getUserWorkflowFn]);

  const widgetUserWorkflowActivityItems = useMemo(
    () =>
      data
        ? data.items.map(({ action, activityType, details, createdAt, personName, personId, id, type }) => ({
          action: {
            action,
            activityType,
            type,
          },
          details,
          id,
          time: createdAt,
          user: {
            id: personId,
            name: personName,
          },
        }))
        : [],
    [data],
  );

  if (!userWorkflowData) {
    return (
      <>
        <Spacer size={40} />
        <ProgressIndicator isCentered />
      </>
    );
  }

  return (
    <>
      <Header
        title="Workflow Activity"
        description={`${userWorkflowData.person.name} · ${userWorkflowData.workflow.name}`}
        actions={[
          {
            icon: Note,
            label: 'Add case note',
            onClick: () => setIsNoteModalOpen(true),
          },
        ]}
      />
      <Filter
        values={search}
        setValues={setSearch}
        filters={FILTERS}
        selectedFilterIndex={selectedFilterIndex}
        setSelectedFilterIndex={setSelectedFilterIndex}
        resetData={() => {}}
        displayDatePickers={false}
        displaySearch={false}
      />
      <Table>
        <THead>
          <TableRow>
            {columns.map(c => (
              <TH key={c} label={capitalize(c)} />
            ))}
          </TableRow>
        </THead>
        {fetchingState.isFetched && (
          <TBody>
            {widgetUserWorkflowActivityItems.map(item => (
              <MemoizedItemRow key={item.id} item={item} />
            ))}
          </TBody>
        )}
      </Table>
      {fetchingState.isFetching && (
        <>
          <Spacer size={40} />
          <ProgressIndicator isCentered />
        </>
      )}
      {isNoteModalOpen && (
        <AddUserWorkflowNoteModal
          collaborators={collaborators || []}
          userWorkflowId={userWorkflowId}
          onClose={() => setIsNoteModalOpen(false)}
          onAddComment={async () => {
            // Need to delay, since activity is added on event listener
            await Bluebird.delay(250);
            await getUserWorkflowActivityById({
              endDate: search.endDate || undefined,
              filter: FILTERS[selectedFilterIndex].value,
              page: 0,
              perPage: 1000,
              search: search.searchBy,
              startDate: search.startDate || undefined,
              userWorkflowId,
            });
          }}
        />
      )}
    </>
  );
};

export default UserWorkflowActivityThread;
